/**
 * Figma flow 9 "Report a problem after a ride" (R1–R6): parts, problem kinds, steps and route
 * params. Client-side only: the end of the flow sends nothing (no Pony report API yet).
 * Ids are stable codes for a future backend; the French labels are display only.
 */

export type PartId =
  | "brake"
  | "wheel"
  | "tyre"
  | "chain"
  | "pedal"
  | "steering"
  | "saddle"
  | "kickstand"
  | "bell"
  | "qr_code"
  | "light"
  | "phone_holder"
  | "grip"
  | "assist";

export interface Part {
  id: PartId;
  label: string;
  /** Text emoji, as drawn (system font). */
  emoji: string;
}

/** Figma R3b order; the first `COMMON_PARTS` are the R3 grid. */
export const PARTS: readonly Part[] = [
  { id: "brake", label: "Frein", emoji: "🛑" },
  { id: "wheel", label: "Roue", emoji: "🛞" },
  { id: "tyre", label: "Pneu", emoji: "⭕" },
  { id: "chain", label: "Chaîne", emoji: "⛓️" },
  { id: "pedal", label: "Pédale", emoji: "🦶" },
  { id: "steering", label: "Direction", emoji: "↔️" },
  { id: "saddle", label: "Selle", emoji: "💺" },
  { id: "kickstand", label: "Béquille", emoji: "🦵" },
  { id: "bell", label: "Sonnette", emoji: "🔔" },
  { id: "qr_code", label: "QR code", emoji: "🔳" },
  { id: "light", label: "Lumière", emoji: "💡" },
  { id: "phone_holder", label: "Porte-téléphone", emoji: "📱" },
  { id: "grip", label: "Poignée", emoji: "✋" },
  { id: "assist", label: "Assistance", emoji: "⚡" },
];
const COMMON_PARTS = 6;

export type PartChip =
  | { kind: "part"; part: Part }
  | { kind: "more"; label: "Voir plus…"; emoji: "➕" };

/** R3: the most common parts then « Voir plus… »; R3b (`showAll`): the 14 parts. */
export function partChips(showAll: boolean): PartChip[] {
  const parts = (showAll ? PARTS : PARTS.slice(0, COMMON_PARTS)).map((part): PartChip => ({
    kind: "part",
    part,
  }));
  return showAll ? parts : [...parts, { kind: "more", label: "Voir plus…", emoji: "➕" }];
}

export type KindId = "not_working" | "broken" | "noise" | "unknown" | "other";

/**
 * R4 radio list. `recap` is the R5 line after the part name: only « un dommage signalé » is drawn
 * (for « C'est cassé ou abîmé »), the others are inferred.
 */
export const KINDS: readonly { id: KindId; label: string; recap: string }[] = [
  { id: "not_working", label: "Ça ne fonctionne pas", recap: "un dysfonctionnement signalé" },
  { id: "broken", label: "C'est cassé ou abîmé", recap: "un dommage signalé" },
  { id: "noise", label: "Ça fait du bruit", recap: "un bruit signalé" },
  { id: "unknown", label: "Je ne sais pas", recap: "un problème signalé" },
  { id: "other", label: "Autre", recap: "un problème signalé" },
];

export interface Reported {
  part: PartId;
  kind: KindId;
}

const partOf = (id: PartId) => PARTS.find((p) => p.id === id)!;

/** R4 « Confirmer »: adds the part, or changes its kind where it already is. */
export function setReported(list: readonly Reported[], part: PartId, kind: KindId): Reported[] {
  return list.some((r) => r.part === part)
    ? list.map((r) => (r.part === part ? { part, kind } : r))
    : [...list, { part, kind }];
}

export function kindOf(list: readonly Reported[], part: PartId): KindId | undefined {
  return list.find((r) => r.part === part)?.kind;
}

/** R3 « Envoyer » is disabled until a part is reported (R5); the free text is optional. */
export function canSend(list: readonly Reported[]): boolean {
  return list.length > 0;
}

/** R5 lines: « Roue » (violet) + « : un dommage signalé ». */
export function recapLines(list: readonly Reported[]): { part: string; text: string }[] {
  return list.map((r) => ({
    part: partOf(r.part).label,
    text: ` : ${KINDS.find((k) => k.id === r.kind)!.recap}`,
  }));
}

/** R6 card, under the bike: the reported parts. */
export function partsSummary(list: readonly Reported[]): string {
  return list.map((r) => partOf(r.part).label).join(", ");
}

/** R2 intro → R3 parts (R3b, R4 and R5 are states of it) → R6 thanks. */
export type ReportStep = "intro" | "parts" | "thanks";

export function stepAfter(step: "intro" | "parts"): ReportStep {
  return step === "intro" ? "parts" : "thanks";
}

/** App-bar back / close: R3 goes back to R2; R2 (close) and R6 leave the flow. */
export function stepBefore(step: ReportStep): ReportStep | "exit" {
  return step === "parts" ? "intro" : "exit";
}

/** R1 options. */
export const CHOICES = [
  { id: "choice-bike", label: "Un problème avec le vélo" },
  { id: "choice-other", label: "L'app, le paiement, un remboursement…" },
] as const;

export type ReportExit = (typeof CHOICES)[number]["id"] | "intro-other";

/** Exits the design doesn't draw (support, other problem): the « coming soon » toast. */
export function isComingSoon(exit: ReportExit): boolean {
  return exit !== "choice-bike";
}

// ---- Route params ------------------------------------------------------------------------------

export const REPORT_PATH = "/signaler";
const DEFAULT_BACK = "/map";
/** Pony ids are opaque (CLAUDE.md). */
const ID = /^[A-Za-z0-9_-]{1,200}$/;
/** In-app path only: one leading slash, then URL-safe path characters (no scheme, no `//`, no `\`). */
const BACK = /^\/(?!\/)[A-Za-z0-9_\-./]{0,199}$/;

export interface ReportParams {
  vehicle?: string;
  trip?: string;
  /** Where « Terminer » and the close X go. */
  back: string;
}

type Raw = string | string[] | undefined;
const first = (v: Raw) => (Array.isArray(v) ? v[0] : v);

/** Route query → validated values; anything invalid is dropped (back defaults to the map). */
export function parseReportParams(q: { vehicle?: Raw; trip?: Raw; back?: Raw }): ReportParams {
  const vehicle = first(q.vehicle);
  const trip = first(q.trip);
  const back = first(q.back);
  return {
    ...(vehicle !== undefined && ID.test(vehicle) ? { vehicle } : {}),
    ...(trip !== undefined && ID.test(trip) ? { trip } : {}),
    back: back !== undefined && BACK.test(back) ? back : DEFAULT_BACK,
  };
}

/** Link to the flow (R2), e.g. from R1 « Un problème avec le vélo » or the menu. */
export function reportHref(p: { vehicle?: string; trip?: string; back?: string }): string {
  const ok = parseReportParams(p);
  const q = new URLSearchParams();
  if (ok.vehicle) q.set("vehicle", ok.vehicle);
  if (ok.trip) q.set("trip", ok.trip);
  if (p.back !== undefined && ok.back === p.back) q.set("back", ok.back);
  const s = q.toString();
  return s ? `${REPORT_PATH}?${s}` : REPORT_PATH;
}
